/**
 * Platform helpers. "qr" is WhatsApp linked by QR (Baileys): in the chat itself it behaves like WhatsApp (same
 * message kinds, same document/name handling), but it is its own platform so it can carry its own badge and is kept
 * out of Meta-only features (templates, broadcasts, calling, catalog). Use isWhatsAppStyle() where a check means
 * "WhatsApp-like chat" and keep a strict `=== "whatsapp"` where a feature is Meta-only.
 */
export const isQrPlatform = (platform) => platform === "qr";

/** WhatsApp (Meta) or WhatsApp linked by QR. A missing platform counts as WhatsApp, as everywhere else. */
export const isWhatsAppStyle = (platform) => platform === undefined || platform === null || platform === "whatsapp" || platform === "qr";

/**
 * Is this chat (an inbox card, an open chat's info, or a message thread) a QR chat? It is when its platform is "qr" OR it
 * carries a QR number (`connectionId` / `lastConnectionId`) -- the number is the stronger signal, since a card built from a live
 * event can briefly lack its platform. Meta / Instagram / Facebook / website chats never have a number.
 * One rule for every per-chat "QR vs Meta" decision (templates, 24h window, block, calling ...).
 */
export const isQrChat = (chat) => Boolean(chat) && (chat.platform === "qr" || Boolean(chat.connectionId || chat.lastConnectionId));

/**
 * Is this open thread a Meta (WhatsApp Cloud API) CONTACT's chat, judged only by the chat itself -- not by the workspace? A workspace that has
 * QR numbers but no connected Meta number counts as "QR-only" for the navigation, yet a Meta contact's chat in it is still a Meta chat (the
 * coexistence "Reply via" choice depends on this: with Meta disconnected and the linked QR number up, replies go through QR). False for a
 * QR chat (platform "qr", or it carries a QR number), a group, a team-member thread and every other platform.
 */
export const isMetaContactThread = ({ platform, isGroup = false, isTeamMemberChat = false, isQrSpecificChat = false } = {}) =>
  !isTeamMemberChat && !isQrSpecificChat && !isGroup && (platform === "whatsapp" || platform === undefined || platform === null);

/**
 * Does this chat follow Meta's 24-hour reply window? Website chat never does, and neither does a WhatsApp-style chat of a QR-connected workspace
 * (a QR number is a normal WhatsApp account). `isQrConnectedWorkspace` is the WORKSPACE flag ("QR-only": it has a QR number and no connected Meta
 * number), but such a workspace can also hold Instagram and Facebook chats, which keep their 24-hour window exactly as in any other workspace.
 * Judged by the chat's own platform so the workspace flag alone never switches the window off for a chat that is not a WhatsApp chat.
 */
export const hasMetaServiceWindow = ({ platform, isQrConnectedWorkspace = false } = {}) =>
  platform !== "website" && !(Boolean(isQrConnectedWorkspace) && isWhatsAppStyle(platform));

